// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { GetProductCollectionRealTimeInventoryApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { getProductCollectionRegionalInventory } from "@mcmc/App/Services/InventoryService";

type Parameter = GetProductCollectionRealTimeInventoryApiV2Parameter &
    HasOnSuccess<RealTimeInventoryModel> &
    HasOnError<unknown>;

type Props = {
    apiParameter: GetProductCollectionRealTimeInventoryApiV2Parameter;
    apiResult: RealTimeInventoryModel;
    error?: unknown;
};

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, ...apiParameter } = props.parameter;
    props.apiParameter = apiParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    const session = getSession(props.getState());
    const isAutenticated = session.isAuthenticated || session.rememberMe;

    if (!getSettingsCollection(props.getState()).productSettings.showInventoryAvailability || !isAutenticated) {
        return;
    }

    props.dispatch({
        type: "xProductDetails/SetLoadingRegionalInventory",
        ids: props.apiParameter.productIds,
    } as any);

    try {
        props.apiResult = await getProductCollectionRegionalInventory(props.apiParameter);
        props.dispatch({
            type: "xProductDetails/SetRegionalInventory",
            results: props.apiResult,
        } as any);
    } catch (error) {
        logger.warn(`Failed to load inventory data: ${error}`);
        props.error = error;
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.error) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.error);
    }
};

export const chain = [PopulateApiParameter, RequestDataFromApi, ExecuteOnSuccessCallback, ExecuteOnErrorCallback];

const loadRegionalInventory = createHandlerChainRunner(chain, "LoadRegionalInventory");

export default loadRegionalInventory;
